/**
 * 65.31 step 2 (the owner: "a user inventory that's configurable"; the row: "drawn down like grenadeStock and restocked at
 * a crate or FOB"): the 40 mm's rounds, on 65.29 (2)'s pattern (core/rocketStock.ts).
 * - A unit's issue is its rounds a life (`gl40Issue`: its slot's count, else GameConfig.gl40Rounds; absent, the launcher's
 *   own — one in the breech and GROUND_WEAPONS.gl40.reserve spares).
 * - With GameConfig.gl40Stock, its side's stock (`state.gl40Stocks`) starts at GL40_STOCK.refills issues for every carrier
 *   the side starts with; a respawn draws the unit's issue from it as far as it goes (the breech first, then the spares),
 *   and a team crate or a linked FOB in reach tops its spares back up from it when the unit asks.
 * Pure; with the switch off no side has a stock and a respawn issues the unit its rounds whole.
 */
import type { GameState, PlayerState } from '../types';
import { GROUND_WEAPONS, GL40_STOCK } from '../constants/action';
import { nearestCrate, crateSide, supplyReach } from './supply';
import { standingCrate } from './grenadeStock';
import { servesPool } from './fob';
import { carriesGl40 } from './loadout';
import { defaultOn } from './defaultOn';

export type Gl40Event = { kind: 'gl40Restocked'; unitId: number; rounds: number; at: 'respawn' | 'crate' };

const BREECH = GROUND_WEAPONS.gl40.magazine, SPARE = GROUND_WEAPONS.gl40.reserve ?? 0;

/** A unit's rounds a life: its issue, else the launcher's own. */
export function gl40IssueOf(p: Pick<PlayerState, 'gl40Issue'>): number {
  return p.gl40Issue ?? BREECH + SPARE;
}

/** The spares a unit is issued (its rounds a life past the breech). */
function sparesOf(p: Pick<PlayerState, 'gl40Issue'>): number {
  return Math.max(0, gl40IssueOf(p) - BREECH);
}

/** The rounds a unit carries: in the breech and spare (absent = its issue's). */
export function gl40Carried(p: Pick<PlayerState, 'magazines' | 'reserves' | 'gl40Issue'>): number {
  return (p.magazines?.gl40 ?? Math.min(BREECH, gl40IssueOf(p))) + (p.reserves?.gl40 ?? sparesOf(p));
}

/** A unit's issue, whole: the breech and its spares (no stock: the setup, a respawn with the switch off). */
export function issueGl40Whole(p: PlayerState): PlayerState {
  if (p.gl40Issue === undefined || !carriesGl40(p)) return p;
  const n = gl40IssueOf(p);
  return { ...p, magazines: { ...(p.magazines ?? {}), gl40: Math.min(BREECH, n) }, reserves: { ...(p.reserves ?? {}), gl40: Math.max(0, n - BREECH) } };
}

/** The setup's sizing (the switch on): each side's stock is `refills` issues for every carrier it starts with. */
export function withGl40Stocks(state: GameState): GameState {
  if (!defaultOn(state.config, 'gl40Stock')) return state;
  const stocks: Record<number, number> = {};
  for (const p of state.players) if (carriesGl40(p)) stocks[p.teamIndex] = (stocks[p.teamIndex] ?? 0) + Math.floor(gl40IssueOf(p) * GL40_STOCK.refills);
  return Object.keys(stocks).length ? { ...state, gl40Stocks: stocks } : state;
}

/** A respawned carrier's issue: from its side's stock where there is one (the breech first, then the spares; the event says what it got), else whole. */
export function respawnGl40(state: GameState, unitId: number): { state: GameState; event: Gl40Event | null } {
  const p = state.players.find(x => x.id === unitId);
  if (!p || !carriesGl40(p)) return { state, event: null };
  const stock = state.gl40Stocks?.[p.teamIndex];
  if (stock === undefined) return p.gl40Issue === undefined ? { state, event: null } : { state: { ...state, players: state.players.map(x => (x.id === unitId ? issueGl40Whole(x) : x)) }, event: null };
  const got = Math.min(gl40IssueOf(p), stock), breech = Math.min(BREECH, got), spare = got - breech;
  const players = state.players.map(x => (x.id === unitId ? { ...x, magazines: { ...(x.magazines ?? {}), gl40: breech }, reserves: { ...(x.reserves ?? {}), gl40: spare } } : x));
  return { state: { ...state, players, gl40Stocks: { ...state.gl40Stocks, [p.teamIndex]: stock - got } }, event: got > 0 ? { kind: 'gl40Restocked', unitId, rounds: got, at: 'respawn' } : null };
}

/** Short of its spares, and the side's stock still holds some: worth asking for at a crate in reach. */
export function gl40Wanted(state: Pick<GameState, 'gl40Stocks'>, p: Pick<PlayerState, 'reserves' | 'teamIndex' | 'gl40Issue' | 'secondary' | 'carry'>): boolean {
  return carriesGl40(p) && (state.gl40Stocks?.[p.teamIndex] ?? 0) > 0 && (p.reserves?.gl40 ?? sparesOf(p)) < sparesOf(p);
}

/** One tick's crate top-ups of the spares from the stock: every carrier asking, on its feet and out of any seat, within reach of a team crate (a FOB only while linked). Null when nothing changed. */
export function stepGl40Restock(state: GameState, asks: ReadonlyArray<boolean | undefined>): { state: GameState; events: Gl40Event[] } | null {
  if (!state.gl40Stocks) return null;
  let next = state;
  const events: Gl40Event[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = next.players[i];
    if (!asks[i] || !p.alive || p.airborne || p.mount) continue;
    const crate = nearestCrate(next, p, supplyReach(next), s => standingCrate(s) && servesPool(next, s));
    const side = crate ? crateSide(next, crate, p) : p.teamIndex;   // (242: any side's crate, its side's stock)
    if (!crate || !gl40Wanted(next, { ...p, teamIndex: side })) continue;
    const stock = next.gl40Stocks![side], spares = sparesOf(p), give = Math.min(spares - (p.reserves?.gl40 ?? spares), stock);
    next = {
      ...next,
      players: next.players.map(x => (x.id === p.id ? { ...x, reserves: { ...(x.reserves ?? {}), gl40: (x.reserves?.gl40 ?? spares) + give } } : x)),
      gl40Stocks: { ...next.gl40Stocks, [side]: stock - give },
    };
    events.push({ kind: 'gl40Restocked', unitId: p.id, rounds: give, at: 'crate' });
  }
  return events.length ? { state: next, events } : null;
}
